import React, { useState, useEffect, useCallback, useRef } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { listAuditEvents } from "./accessManagerApi";
import "./AccessManagerPanel.css";

/** Selectable page sizes for the audit log. */
export const LIMIT_OPTIONS = [25, 50, 100];

/** Default number of events requested when the drawer opens. */
export const DEFAULT_LIMIT = 50;

/** Debounce delay (ms) applied to the email filter input. */
const FILTER_DEBOUNCE_MS = 300;

/**
 * Normalizes the audit API response into an array of events.
 * Tolerates a bare array or an object wrapper, matching the other components.
 *
 * @param {*} data - The `response.data` payload.
 * @returns {Array<Object>}
 */
function extractEvents(data) {
  if (Array.isArray(data)) return data;
  return data?.events ?? data?.audit ?? data?.data ?? [];
}

/**
 * Formats an ISO timestamp for display, falling back to the raw value.
 *
 * @param {string} timestamp
 * @returns {string}
 */
function formatTimestamp(timestamp) {
  if (!timestamp) return "—";
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return String(timestamp);
  return parsed.toLocaleString();
}

/**
 * AuditLogDrawer — slide-in panel listing access grant/revoke audit events.
 *
 * Fetches events from `listAuditEvents({ limit, email })` when opened and
 * re-fetches whenever the email filter or limit selector changes. Closes via
 * overlay click, the close button, or the Escape key (handled by Radix Dialog).
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the drawer is visible.
 * @param {() => void} props.onClose - Called when the drawer should close.
 */
export default function AuditLogDrawer({ open, onClose }) {
  const [events, setEvents] = useState([]);
  const [emailFilter, setEmailFilter] = useState("");
  const [appliedEmail, setAppliedEmail] = useState("");
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Guards against stale responses overwriting newer state.
  const requestIdRef = useRef(0);

  // Debounce typing in the email filter before it triggers a re-fetch.
  useEffect(() => {
    const trimmed = emailFilter.trim();
    if (trimmed === appliedEmail) return undefined;
    const timer = setTimeout(() => setAppliedEmail(trimmed), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [emailFilter, appliedEmail]);

  const fetchEvents = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await listAuditEvents({ limit, email: appliedEmail });
      if (requestId !== requestIdRef.current) return;
      setEvents(extractEvents(response?.data));
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to load audit events.";
      setEvents([]);
      setError(message);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [limit, appliedEmail]);

  // Fetch on open and whenever the filter or limit changes while open.
  useEffect(() => {
    if (!open) return;
    fetchEvents();
  }, [open, fetchEvents]);

  // Reset state when the drawer closes so it reopens clean.
  useEffect(() => {
    if (open) return;
    requestIdRef.current += 1;
    setEvents([]);
    setEmailFilter("");
    setAppliedEmail("");
    setLimit(DEFAULT_LIMIT);
    setError(null);
    setLoading(false);
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="access-manager-audit__overlay" />
        <Dialog.Content className="access-manager-audit__drawer">
          <div className="access-manager-audit__header">
            <Dialog.Title className="access-manager-audit__title">
              Access Audit Log
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="access-manager-audit__close-btn"
                aria-label="Close audit log"
              >
                Close
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Description className="access-manager-audit__description">
            Chronological history of scope grants and revocations.
          </Dialog.Description>

          <div className="access-manager-audit__filters">
            <div className="access-manager-audit__filter">
              <label
                htmlFor="audit-email-filter"
                className="access-manager-audit__filter-label"
              >
                Filter by email
              </label>
              <input
                id="audit-email-filter"
                type="text"
                className="access-manager-audit__filter-input"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                placeholder="user@company.com"
                autoComplete="off"
              />
            </div>

            <div className="access-manager-audit__filter">
              <label
                htmlFor="audit-limit-select"
                className="access-manager-audit__filter-label"
              >
                Limit
              </label>
              <select
                id="audit-limit-select"
                className="access-manager-audit__filter-select"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                {LIMIT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="access-manager-audit__body">
            {loading ? (
              <div className="access-manager-audit__loading">
                Loading audit events…
              </div>
            ) : error ? (
              <div className="access-manager-panel__error" role="alert">
                <p className="access-manager-audit__error-message">{error}</p>
                <button
                  type="button"
                  className="access-manager-audit__retry-btn"
                  onClick={fetchEvents}
                >
                  Retry
                </button>
              </div>
            ) : events.length === 0 ? (
              <div className="access-manager-audit__empty">
                No audit events found.
              </div>
            ) : (
              <table className="access-manager-audit__table">
                <thead>
                  <tr>
                    <th scope="col">Timestamp</th>
                    <th scope="col">Actor</th>
                    <th scope="col">Target</th>
                    <th scope="col">Scope</th>
                    <th scope="col">Action</th>
                    <th scope="col">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event, index) => (
                    <tr key={`${event.timestamp}-${index}`}>
                      <td>{formatTimestamp(event.timestamp)}</td>
                      <td>{event.actor_email || "—"}</td>
                      <td>{event.target_email || "—"}</td>
                      <td>{event.scope || "—"}</td>
                      <td>{event.action || "—"}</td>
                      <td>{event.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
